import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { QrCode, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// Sprint 12.0 — Escáner QR por cámara (móvil). Enciende el feed, decodifica y devuelve el folio a onResult.
export const QrCamScanner = ({ open, onClose, onResult }) => {
  const regionId = "valet-qr-region";
  const scannerRef = useRef(null);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    let stopped = false;
    setError("");
    const qrbox = (vw, vh) => { const m = Math.floor(Math.min(vw, vh) * 0.7); return { width: m, height: m }; };
    const startWhenReady = (attempt = 0) => {
      if (stopped) return;
      const el = document.getElementById(regionId);
      if (!el) {
        if (attempt < 25) { setTimeout(() => startWhenReady(attempt + 1), 60); return; }
        setError("No se pudo iniciar la cámara. Intenta de nuevo.");
        return;
      }
      const h = new Html5Qrcode(regionId, { verbose: false });
      scannerRef.current = h;
      h.start(
        { facingMode: "environment" },
        { fps: 10, qrbox, aspectRatio: 1.0 },
        (decodedText) => {
          if (stopped) return;
          stopped = true;
          h.stop().then(() => h.clear()).catch(() => {});
          onResultRef.current(decodedText);
        },
        () => {} // ignora errores de decodificación por frame (ruido normal)
      ).catch(() => setError("No se pudo acceder a la cámara. Revisa los permisos del navegador."));
    };
    startWhenReady();
    return () => {
      stopped = true;
      const s = scannerRef.current;
      if (s) { try { s.stop().then(() => s.clear()).catch(() => {}); } catch {} }
    };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none border-[#FFCC00]/40 bg-[#0A0A0A] text-white max-w-md max-h-[90dvh] overflow-y-auto overflow-x-hidden" data-testid="valet-qr-cam-modal">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-white flex items-center gap-2"><QrCode className="h-5 w-5 text-[#FFCC00]" /> Escanear QR</DialogTitle>
          <DialogDescription className="text-white/50">Apunta la cámara al código QR del boleto.</DialogDescription>
        </DialogHeader>
        <div className="relative w-full">
          <div id={regionId} className="w-full overflow-hidden bg-black min-h-[260px] [&_video]:w-full [&_video]:object-cover" data-testid="valet-qr-region" />
          {!error && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="h-44 w-44 border-2 border-[#FFCC00] shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" />
            </div>
          )}
        </div>
        {error && <p className="text-[#EF4444] text-sm font-semibold" data-testid="valet-qr-error">{error}</p>}
        <button onClick={onClose} data-testid="valet-qr-cancel-btn"
          className="btn-hard w-full h-12 mt-2 bg-white/5 border border-white/20 text-white font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-white/10">
          <X className="h-5 w-5" /> Cancelar / Cerrar cámara
        </button>
      </DialogContent>
    </Dialog>
  );
};

export default QrCamScanner;
